import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/store/modules/user'
import { useImageFeedbackStore, PENDING_FEEDBACK_ERROR_CODE } from '@/stores/image-feedback'
import router from '@/router'

// 确保 URL 使用 HTTPS
function ensureHttps(obj) {
  if (typeof obj === 'string' && obj.startsWith('http://')) {
    return obj.replace('http://', 'https://');
  } else if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach(key => {
      obj[key] = ensureHttps(obj[key]);
    });
  }
  return obj;
}

// 创建axios实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    const userStore = useUserStore()
    const latestToken = localStorage.getItem('token') || ''

    if (latestToken !== userStore.token) {
      if (latestToken) {
        userStore.token = latestToken
      } else if (userStore.token) {
        userStore.resetState()
      }
    }

    if (latestToken) {
      config.headers['Authorization'] = `Bearer ${latestToken}`
    }
    const params = config.params || {}

    // get参数编码
    if (config.method?.toUpperCase() === 'GET' && params) {
      let url = config.url + '?'
      for (const propName of Object.keys(params)) {
        const value = params[propName]
        if (value !== void 0 && value !== null && typeof value !== 'undefined') {
          if (typeof value === 'object') {
            for (const val of Object.keys(value)) {
              const params = propName + '[' + val + ']'
              const subPart = encodeURIComponent(params) + '='
              url += subPart + encodeURIComponent(value[val]) + '&'
            }
          } else {
            url += `${propName}=${encodeURIComponent(value)}&`
          }
        }
      }
      // 给 get 请求加上时间戳参数，避免从缓存中拿数据
      // const now = new Date().getTime()
      // params = params.substring(0, url.length - 1) + `?_t=${now}`
      url = url.slice(0, -1)
      config.params = {}
      config.url = url
    }
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const config = response.config
    const responseType = config.responseType || config.headers?.responseType

    // 文件下载
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      // 检查 JSON 错误
      const blob = response.data
      if (blob instanceof Blob && blob.type === 'application/json') {
        // 尝试读取内容判断是否为错误信息
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result)
              ElMessage.error(json.msg || '操作失败')
              reject(new Error(json.msg || '操作失败'))
            } catch (e) {
              resolve(response)
            }
          }
          reader.onerror = () => resolve(response)
          reader.readAsText(blob)
        })
      }
      // 正常文件流，直接返回 response
      return Promise.resolve(response)
    }

    const { code, data, msg } = response.data
    
    // 请求成功
    if (code === 0) {
      return response.data
    }

    if (config.silentError) {
      return Promise.reject(Object.assign(new Error(msg || '请求失败'), { code }))
    }
    
    if (code === 401) {
      const userStore = useUserStore()
      userStore.resetState()
      router.replace({ path: '/login', query: { redirect: router.currentRoute.value.fullPath } })
      ElMessage.error(msg || '操作失败')
      return Promise.reject(new Error(msg || '操作失败'))
    }

    if (code == 400 && msg == '无效的刷新令牌') {      
      router.replace({ path: '/login', query: { redirect: router.currentRoute.value.fullPath } })
      return 
    }

    // 待反馈错误码 - 弹出确认框让用户选择是否去反馈
    if (code === PENDING_FEEDBACK_ERROR_CODE) {
      const imageFeedbackStore = useImageFeedbackStore()
      ElMessageBox.confirm(
        msg || '存在未反馈的生图结果，请先完成反馈后再继续操作。',
        '提示',
        {
          confirmButtonText: '去反馈',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        imageFeedbackStore.openFeedbackCollector(msg)
      }).catch(() => {
        // 用户取消
      })
      return Promise.reject(new Error(msg || '待反馈'))
    }

    // 业务错误
    console.error('响应错误:', msg)
    // ElMessage.error(msg || '操作失败')

    const businessError = new Error(msg || '操作失败')
    businessError.code = code
    businessError.msg = msg
    businessError.data = data
    businessError.response = response.data

    return Promise.reject(businessError)
  },
  error => {
    if (error.config?.silentError) return Promise.reject(error)
    const userStore = useUserStore()
    
    if (error.response) {
      const { status, data } = error.response
      
      // token过期或无效
      if (status === 401) {
        ElMessage.error('登录已过期，请重新登录')
        userStore.resetState()
        window.location.reload()
        return Promise.reject(error)
      }
      
      // 权限不足
      if (status === 403) {
        ElMessage.error('没有操作权限')
        return Promise.reject(error)
      }
      
      // 其他错误
      ElMessage.error(data?.msg || '系统错误')
    } else {
      ElMessage.error('网络连接失败')
    }
    
    return Promise.reject(error)
  }
)

export default service
